/* COPY AN EXERCISE — the ONE implementation, called by the exercise builder's Copy and by the Library row's hover
 * `Copy` (W72 step 5), and held EQUAL to the phone's `CoachLibraryFacts.copyRow` on the corpus the Swift emits
 * (`coachLibraryCorpus.test.ts`, W83-4).
 *
 * 🔴 A COPY IS A COPY — THE WHOLE ROW (Fable's ruling, W83-4; the codex's V7-2 lesson: "a copy must see the whole row and
 * FAIL CLOSED against schema growth"). The first version read the builder's select and wrote the builder's columns, so a
 * copied HOLD lost `is_hold` (97 live holds; the column defaults to false, and the phone's builder then prescribes reps),
 * the typical ranges and the catalogue link. Now the source is read with `select *` and every column is carried except the
 * NAMED four in `COPY_NOT_CARRIED`; the copy SETS four (`COPY_SETS`). A column added tomorrow is carried, never dropped —
 * `coachLibraryCorpus.test.ts` asserts the carried set against this file's generated `database.types.ts`.
 */
import type { Json, TablesInsert } from "./database.types";
import { copyName } from "./exerciseAutosave";

/** The whole row. */
export const COPY_SELECT = "*";

/** The columns a copy does NOT carry — `id` (the copy's own), `created_at`/`updated_at` (the copy's own times), and
 *  `starter_key` (a copy is not a starter row: a starter refresh must never reach in and rewrite it). The Swift list is
 *  the same four, pinned on the corpus. */
export const COPY_NOT_CARRIED = ["id", "created_at", "updated_at", "starter_key"] as const;

/** The columns a copy SETS rather than reads: the copier, the free name, back on the shelf, the video's provenance. */
export const COPY_SETS = ["coach_id", "name", "hidden_at", "demo_video_source"] as const;

type SourceRow = Record<string, unknown> & { name: string; demo_video_url?: string | null; demo_video_source?: string | null };

export type CopyDeps = {
  read: (id: string) => Promise<{ row: SourceRow | null; error: string | null }>;
  /** The coach's exercise names — the copy takes the first free one (W83-4). */
  names: (coachId: string) => Promise<{ names: string[] | null; error: string | null }>;
  insert: (row: Record<string, unknown>) => Promise<{ id: string | null; error: string | null }>;
};

export type CopyOutcome = { ok: true; id: string; name: string } | { ok: false; message: string };

/** The row to insert for a copy of `source`: the source WHOLE minus `COPY_NOT_CARRIED`, under a free name, owned by the
 *  copier, on the shelf, with the video's PROVENANCE read from the source only when the copy carries a video. Pure. */
export function copyRow(source: SourceRow, coachId: string, taken: ReadonlySet<string>): Record<string, unknown> & { name: string } {
  const out: Record<string, unknown> = { ...source };
  for (const c of COPY_NOT_CARRIED) delete out[c];
  const video = typeof source.demo_video_url === "string";
  return {
    ...out,
    coach_id: coachId,
    name: copyName(source.name, taken),
    hidden_at: null,
    demo_video_source: video ? (source.demo_video_source ?? null) : null,
  };
}

export async function copyExercise(deps: CopyDeps, id: string, coachId: string): Promise<CopyOutcome> {
  const read = await deps.read(id);
  if (read.error !== null || read.row === null) {
    return { ok: false, message: `Could not copy: ${read.error ?? "the exercise was not found."}` };
  }
  const held = await deps.names(coachId);
  if (held.error !== null || held.names === null) {
    return { ok: false, message: `Could not copy ${read.row.name}: ${held.error ?? "your exercise names could not be read."}` };
  }
  const row = copyRow(read.row, coachId, new Set(held.names.map((n) => n.toLowerCase())));
  const made = await deps.insert(row);
  if (made.error !== null || made.id === null) {
    return { ok: false, message: `Could not copy ${read.row.name}: ${made.error ?? "the copy returned no id."}` };
  }
  return { ok: true, id: made.id, name: row.name };
}

/** The live deps over a Supabase client (passed in, so this module imports no client). */
export function copyDepsFrom(client: typeof import("./supabase").supabase): CopyDeps {
  return {
    read: async (id) => {
      const { data, error } = await client.from("coach_exercises").select(COPY_SELECT).eq("id", id).maybeSingle();
      return { row: (data as SourceRow | null) ?? null, error: error?.message ?? null };
    },
    names: async (coachId) => {
      const { data, error } = await client.from("coach_exercises").select("name").eq("coach_id", coachId);
      return { names: error === null ? (data ?? []).map((r) => r.name) : null, error: error?.message ?? null };
    },
    insert: async (row) => {
      // The row is `select *` minus the named four plus the set four — every key a column of the table, which the corpus
      // test asserts against the generated types; `Json` for the one jsonb column is the row's own value.
      const insert = row as unknown as TablesInsert<"coach_exercises"> & { progression_config?: Json | null };
      const { data, error } = await client.from("coach_exercises").insert(insert).select("id").single();
      return { id: data?.id ?? null, error: error?.message ?? null };
    },
  };
}
